import { useEffect, useRef, type RefObject } from "react";

/** Either form of "the thing this card is anchored to", so a caller can pass a
 * ref it already holds or an element it captured from an event. */
export type DismissAnchor = RefObject<HTMLElement | null> | Element | null | undefined;

function resolveAnchor(anchor: DismissAnchor): Element | null {
  if (!anchor) return null;
  return "current" in anchor ? anchor.current : anchor;
}

/**
 * The four gestures that mean "not here any more" for a floating card.
 *
 * A menu is anchored to something — a pointer position, a button in a header —
 * and every one of these breaks the anchor: a press somewhere else, a scroll
 * that moves what it was pointing at, a resize that moves everything, and the
 * window losing focus (a ⌘-Tab away should not leave a menu hanging over the
 * app). The right-click menu and the account menu both dismiss on all four, so
 * they behave the same way rather than almost the same way.
 *
 * `refs` are the regions that count as "inside": the card itself, plus the
 * trigger that opened it, so pressing the trigger closes it through its own
 * toggle instead of being closed here and reopened by the click.
 *
 * `options.anchor` is what the card is anchored *to*. Given one, a scroll only
 * dismisses when the thing that scrolled actually contains the anchor. That
 * distinction is not academic: the scroll listener is registered in the capture
 * phase on `window`, so it hears *every* scroll in the app — including the
 * transcript's own per-frame follow scroll while an agent is streaming, which
 * used to shut every menu in the app the instant a chat started producing
 * output (FRNT-693's second cause). Without an anchor the old behaviour stands,
 * so a caller that has nothing to anchor to is no worse off.
 *
 * `onDismiss` is held in a ref, so a caller that rebuilds the callback on every
 * render does not re-register four document-level listeners each time.
 */
export function useDismissOnOutside(
  active: boolean,
  refs: ReadonlyArray<RefObject<HTMLElement | null>>,
  onDismiss: () => void,
  options: { anchor?: DismissAnchor } = {},
): void {
  const dismissRef = useRef(onDismiss);
  const refsRef = useRef(refs);
  const anchorRef = useRef(options.anchor);
  useEffect(() => {
    dismissRef.current = onDismiss;
    refsRef.current = refs;
    anchorRef.current = options.anchor;
  });

  useEffect(() => {
    if (!active) return;
    const inside = (target: EventTarget | null) =>
      refsRef.current.some((ref) => (target instanceof Node ? ref.current?.contains(target) : false));
    const dismiss = () => dismissRef.current();
    const onPointerDown = (event: MouseEvent) => {
      if (!inside(event.target)) dismiss();
    };
    // A card anchored to something in the page is wrong the moment the thing it
    // is anchored to moves. Its own contents scrolling is not that, and neither
    // is an unrelated pane scrolling somewhere else on screen.
    const onScroll = (event: Event) => {
      if (inside(event.target)) return;
      const anchor = resolveAnchor(anchorRef.current);
      if (!anchor) {
        dismiss();
        return;
      }
      const scroller = event.target;
      const moved = scroller instanceof Document
        ? scroller.contains(anchor)
        : scroller instanceof Node && scroller.contains(anchor);
      if (moved) dismiss();
    };
    // Capture, so a click that would open another menu resolves in one gesture.
    document.addEventListener("mousedown", onPointerDown, true);
    window.addEventListener("resize", dismiss);
    window.addEventListener("blur", dismiss);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown, true);
      window.removeEventListener("resize", dismiss);
      window.removeEventListener("blur", dismiss);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [active]);
}
